import { AgFilterComponent } from '@ag-grid-community/angular';
import { IDoesFilterPassParams, IFilterParams, IRowNode } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, HostBinding, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { ActivatedRoute } from '@angular/router';

import {
	DATE_CRITERION_TYPE, DATE_FORMAT_PATTERN,
	FilterDateFormatType, WEEK_FORMAT_PATTERN
} from '../../models/filter-formats';
import { FilterInstance } from '../../services/datacenter-filter';
import { CopoDatepickerInputDirective } from '../../directives/date-picker.directive';
import { DateFilter, FilterType } from '../../models/ag-grid';
import { DatacenterType } from '../../models/datacenter-aggrid-view';
import { ColDefWithFields } from '../column-type';

const SHIPMENT_MULTI_VALUE_DATE_FILTER_NAME =
	<DateFilter['customFilterName']> 'shipment-multi-value-date-filter';

export interface DatacenterFilterParamsInterface extends IFilterParams<Record<string, unknown>, unknown> {
	filterType?: string;
}

@Component({
	selector: 'copo-datacenter-date-filter',
	templateUrl: './datacenter-date-filter.component.html',
	styleUrl: './datacenter-date-filter.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		MatDatepickerModule,

		MatFormFieldModule,

		MatInputModule,
		CopoDatepickerInputDirective,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		TranslateModule
	]
})
export class DatacenterDateFilterComponent implements AgFilterComponent, FilterInstance {
	@HostBinding('class.passthrough') readonly passthrough = true;

	params?: DatacenterFilterParamsInterface;
	fieldName = '';
	translatedFieldName = '';
	formatType: string = DATE_CRITERION_TYPE;
	label = '';
	dateControl = new FormControl<Date | null>(null);
	private isClientSide = false;

	constructor(private readonly route: ActivatedRoute) {}

	agInit(params: DatacenterFilterParamsInterface) {
		this.params = params;
		this.label = this.params.colDef.headerName || '';
		const rowModelType = params.rowModel.getType();
		this.isClientSide = rowModelType === 'clientSide';
	}

	isFilterActive() {
		return Boolean(this.dateControl.value);
	}

	doesFilterPass(_params: IDoesFilterPassParams<Record<string, unknown>>) {
		if (!this.isClientSide) {
			return this.isFilterActive();
		}
		const value = this.getColumnValue(_params.node);
		if (value === undefined) {
			return false;
		}
		return moment(this.dateControl.value).format('DD/MM/YYYY') === moment(value).format('DD/MM/YYYY');
	}

	getModel(): DateFilter | undefined {
		if (this.dateControl.value) {
			this.updateFormatType();
			const pattern = this.formatType === 'week' ? WEEK_FORMAT_PATTERN : DATE_FORMAT_PATTERN;
			return {
				customFilterName: this.getCustomFilterName(),
				date: moment(this.dateControl.value).format(pattern)
			};
		}
		return undefined;
	}

	setModel(date: DateFilter | string | null) {
		if (!date) {
			this.dateControl.reset();
			return;
		}
		this.dateControl.setValue(moment(typeof date === 'string' ? date : date.date).toDate());
	}

	onDateChange() {
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	resetDate() {
		this.dateControl.reset();
		this.dateControl.setValue(null);
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	onTypeChange() {
		this.updateFormatType();
	}

	getCustomFilterName(): DateFilter['customFilterName'] {
		if (this.params?.colDef.filterParams && this.params.filterType
			&& this.params.filterType === 'SHIPMENT_MULTI_VALUE_DATE_FILTER') {
			return SHIPMENT_MULTI_VALUE_DATE_FILTER_NAME;
		}
		if (this.params?.colDef.filterParams && this.params.filterType
			&& this.params.filterType === 'DOCUMENT_DATE_FILTER') {
			return FilterType.DOCUMENT_DATE_FILTER;
		}
		if (this.isShipmentMultiLineColumn()) {
			return SHIPMENT_MULTI_VALUE_DATE_FILTER_NAME;
		}
		return FilterType.DATE_FILTER;
	}

	updateFormatType() {
		if (this.params) {
			const rendererParams = <Partial<FilterDateFormatType> | undefined> this.params.colDef.cellRendererParams;
			this.formatType = rendererParams?.outputFormat ?? DATE_CRITERION_TYPE;
		}
	}

	private getColumnValue(node: IRowNode<Record<string, unknown>>) {
		if (!this.params || typeof node.data !== 'object') {
			return undefined;
		}
		const { data } = node;
		if (typeof this.params.colDef.valueGetter === 'function') {
			const value = <unknown> this.params.colDef.valueGetter({
				api: this.params.api,
				colDef: this.params.colDef,
				column: this.params.column,
				context: this.params.context,
				data,
				node,
				getValue(field: string) {
					return data[field];
				}
			});
			return value ?? undefined;
		} else if (this.params.colDef.field) {
			const value = data[this.params.colDef.field];
			return value ?? undefined;
		}
		return undefined;
	}

	private isShipmentMultiLineColumn() {
		const datacenterType = <DatacenterType | undefined> this.route.snapshot.data['type'];
		const colDef = <ColDefWithFields<Record<string, unknown>> | undefined> this.params?.colDef;
		return datacenterType === 'shipment' && colDef?.valueType !== undefined;
	}
}
